import { ANNOUNCEMENT_KIND_LABELS, type PlatformAnnouncement } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Megaphone, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnnouncementDialog } from "../../features/platform/ops-dialogs.js";
import {
  usePlatformAnnouncements,
  usePlatformPermissions,
  useRefreshPlatform,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { formatDateTime } from "../../lib/format.js";

export const Route = createFileRoute("/platform/duyurular")({ component: AnnouncementsRoute });

const STATUS_TONE = { DRAFT: "neutral", PUBLISHED: "success", ARCHIVED: "neutral" } as const;

/**
 * Duyurular (Prompt 24 web): sürüm notu, duyuru, planlı bakım — tüm kiracılara, plan(lar)a ya da seçili kiracılara.
 * Taslak yayınlanınca kiracıların duyuru merkezine düşer; "önemli" olanlar kabukta şerit olarak görünür.
 */
function AnnouncementsRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.announcements.manage");
  const announcements = usePlatformAnnouncements();
  const [editing, setEditing] = useState<{ announcement: PlatformAnnouncement | null } | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: "publish" | "archive";
    announcement: PlatformAnnouncement;
  } | null>(null);
  const action = useMutation({
    mutationFn: async ({
      kind,
      announcement,
    }: {
      kind: "publish" | "archive";
      announcement: PlatformAnnouncement;
    }) =>
      kind === "publish"
        ? (
            await api.POST("/platform/announcements/{id}/publish", {
              params: { path: { id: announcement.id } },
              body: { publishAt: null, version: announcement.version },
            })
          ).data!
        : (
            await api.POST("/platform/announcements/{id}/archive", {
              params: { path: { id: announcement.id } },
              body: { version: announcement.version },
            })
          ).data!,
    onSuccess: async (_result, variables) => {
      await refresh();
      toast({
        tone: "success",
        title: t(`platform.announcements.${variables.kind === "publish" ? "published" : "archived"}`),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <PlatformPage
      title={t("platform.announcements.title")}
      description={t("platform.announcements.description")}
      permission="platform.catalog.read"
      actions={
        canManage ? (
          <Button onClick={() => setEditing({ announcement: null })}>
            <Plus aria-hidden="true" />
            {t("platform.announcements.new")}
          </Button>
        ) : null
      }
    >
      {announcements.error ? (
        <Alert tone="danger">{errorMessage(announcements.error)}</Alert>
      ) : announcements.isPending ? (
        <SkeletonRows rows={4} />
      ) : announcements.data.length === 0 ? (
        <EmptyState headingLevel={2} icon={<Megaphone />} title={t("platform.announcements.empty")} />
      ) : (
        <ul className="grid gap-3">
          {announcements.data.map((announcement) => (
            <li key={announcement.id}>
              <Card className="grid gap-2 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h2 className="text-base font-semibold">{announcement.title}</h2>
                  <span className="flex flex-wrap gap-1">
                    <StatusBadge tone="info">{ANNOUNCEMENT_KIND_LABELS[announcement.kind]}</StatusBadge>
                    {announcement.severity === "IMPORTANT" ? (
                      <StatusBadge tone="warning">{t("platform.announcements.importantBadge")}</StatusBadge>
                    ) : null}
                    <StatusBadge tone={STATUS_TONE[announcement.status]}>
                      {t(`platform.announcements.status.${announcement.status}`)}
                    </StatusBadge>
                  </span>
                </div>
                <p className="whitespace-pre-line text-sm">{announcement.body}</p>
                <p className="text-xs text-muted-foreground">
                  {announcement.audience === "ALL"
                    ? t("platform.announcements.audience.ALL")
                    : announcement.audience === "PLANS"
                      ? `${t("platform.announcements.audience.PLANS")}: ${announcement.planCodes.join(", ")}`
                      : `${t("platform.announcements.audience.TENANTS")}: ${announcement.tenants.map((tenant) => tenant.name).join(", ")}`}
                  {" · "}
                  {announcement.publishedAt
                    ? t("platform.announcements.publishedAt", {
                        time: formatDateTime(announcement.publishedAt),
                      })
                    : t("platform.announcements.notPublished")}
                  {announcement.expiresAt
                    ? ` · ${t("platform.announcements.expiresAt", { time: formatDateTime(announcement.expiresAt) })}`
                    : ""}
                  {" · "}
                  {t("platform.announcements.readCount", { count: announcement.readCount })}
                </p>
                {canManage && announcement.status !== "ARCHIVED" ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ announcement })}>
                      {t("platform.announcements.editShort")}
                    </Button>
                    {announcement.status === "DRAFT" ? (
                      <Button size="sm" onClick={() => setConfirming({ kind: "publish", announcement })}>
                        {t("platform.announcements.publish")}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirming({ kind: "archive", announcement })}
                    >
                      {t("platform.announcements.archive")}
                    </Button>
                  </div>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <AnnouncementDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          announcement={editing.announcement}
        />
      ) : null}
      {confirming ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirming(null)}
          title={t(`platform.announcements.${confirming.kind}Title`, {
            title: confirming.announcement.title,
          })}
          description={t(`platform.announcements.${confirming.kind}Description`)}
          confirmLabel={t(`platform.announcements.${confirming.kind}`)}
          cancelLabel={t("common.cancel")}
          onConfirm={() =>
            action.mutateAsync(confirming).then(
              () => undefined,
              () => undefined,
            )
          }
        />
      ) : null}
    </PlatformPage>
  );
}
